/**
 * THE client-side guide routing decision.
 *
 * This mirrors `server/src/shared/utils/auth-routing.utils.ts`. Every guard —
 * ProtectedRoute, RoleSurfaceGate, GuideLifecycleGate, BecomeGuidePage — reads
 * from here. Do not add a second place that decides where a guide belongs:
 * the previous implementation had four, keyed off different inputs, and they
 * disagreed.
 */
import {
  GuideLifecycleSummary,
  MarketplaceStatus,
  Permission,
  ProfileStatus,
  User,
  UserRole,
  VerificationStatus,
} from '@/types';
import type { AuthIntent } from '../constants';
import { AUTH_ROUTES, GUIDE_ENTRY_ROUTE } from '../constants';

export const GUIDE_APPLICATION_STATUS_ROUTE = '/guide-application/status';
export const GUIDE_PROFILE_SETUP_ROUTE = '/guide-profile/setup';
export const GUIDE_DASHBOARD_ROUTE = '/dashboard';

const AUTH_PATHS = new Set<string>([
  AUTH_ROUTES.PHONE,
  AUTH_ROUTES.VERIFY_OTP,
  GUIDE_ENTRY_ROUTE,
]);

/** Reads the lifecycle off a user, tolerating older payload shapes. */
export function getGuideLifecycle(user: User | null): GuideLifecycleSummary | null {
  const summary = user?.guideProfile;
  if (!summary) return null;

  if (summary.lifecycle) return summary.lifecycle;

  // A server that has not been redeployed yet only sends the legacy mirror.
  // Degrade to the safest reading rather than guessing someone is approved.
  return {
    verificationStatus: VerificationStatus.PENDING,
    profileStatus: ProfileStatus.NOT_STARTED,
    marketplaceStatus: MarketplaceStatus.NOT_READY,
  };
}

export function hasGuideProfile(user: User | null): boolean {
  return Boolean(user?.guideProfile);
}

export function hasActiveGuideApplication(user: User | null): boolean {
  return hasGuideProfile(user);
}

/** Entitled to the guide dashboard. Not the same as "live on the marketplace". */
export function isApprovedGuide(user: User | null, permissions: Permission[]): boolean {
  const lifecycle = getGuideLifecycle(user);
  if (lifecycle) {
    return (
      lifecycle.profileStatus === ProfileStatus.APPROVED &&
      lifecycle.marketplaceStatus !== MarketplaceStatus.SUSPENDED
    );
  }
  return permissions.includes(Permission.VIEW_GUIDE_DASHBOARD);
}

export function isAwaitingVerification(user: User | null): boolean {
  const lifecycle = getGuideLifecycle(user);
  if (!lifecycle) return false;
  return (
    lifecycle.verificationStatus === VerificationStatus.PENDING ||
    lifecycle.verificationStatus === VerificationStatus.IN_PROGRESS
  );
}

export function isVerificationFailed(user: User | null): boolean {
  return getGuideLifecycle(user)?.verificationStatus === VerificationStatus.FAILED;
}

export function isGuidePendingReview(user: User | null): boolean {
  const lifecycle = getGuideLifecycle(user);
  if (!lifecycle) return false;
  return (
    lifecycle.profileStatus === ProfileStatus.SUBMITTED ||
    lifecycle.profileStatus === ProfileStatus.UNDER_REVIEW
  );
}

export function isSuspendedGuide(user: User | null): boolean {
  return (
    getGuideLifecycle(user)?.marketplaceStatus === MarketplaceStatus.SUSPENDED
  );
}

/** May the guide open the profile setup screen right now? */
export function canCompleteProfile(user: User | null): boolean {
  const lifecycle = getGuideLifecycle(user);
  if (!lifecycle) return false;
  if (lifecycle.verificationStatus !== VerificationStatus.PASSED) return false;
  return (
    lifecycle.profileStatus === ProfileStatus.NOT_STARTED ||
    lifecycle.profileStatus === ProfileStatus.IN_PROGRESS ||
    lifecycle.profileStatus === ProfileStatus.CHANGES_REQUIRED
  );
}

export function needsOnboarding(user: User | null): boolean {
  return canCompleteProfile(user);
}

/**
 * Where a guide-lifecycle user belongs. Returns null when they have no
 * application and should follow the ordinary traveler flow.
 *
 * Order matters: suspension is checked before approval, otherwise a suspended
 * guide would be routed to the dashboard they no longer have access to.
 */
export function resolveGuideLifecycleRoute(user: User | null): string | null {
  const lifecycle = getGuideLifecycle(user);
  if (!lifecycle) return null;

  if (lifecycle.marketplaceStatus === MarketplaceStatus.SUSPENDED) {
    return GUIDE_APPLICATION_STATUS_ROUTE;
  }

  if (lifecycle.verificationStatus !== VerificationStatus.PASSED) {
    return GUIDE_APPLICATION_STATUS_ROUTE;
  }

  switch (lifecycle.profileStatus) {
    case ProfileStatus.NOT_STARTED:
    case ProfileStatus.IN_PROGRESS:
    case ProfileStatus.CHANGES_REQUIRED:
      return GUIDE_PROFILE_SETUP_ROUTE;
    case ProfileStatus.SUBMITTED:
    case ProfileStatus.UNDER_REVIEW:
      return GUIDE_APPLICATION_STATUS_ROUTE;
    case ProfileStatus.APPROVED:
      return GUIDE_DASHBOARD_ROUTE;
    default:
      return GUIDE_APPLICATION_STATUS_ROUTE;
  }
}

/** @deprecated Use `resolveGuideLifecycleRoute`. Kept for existing callers. */
export function resolveGuideDestination(
  user: User | null,
  _permissions: Permission[]
): string {
  if (!user) return '/';
  if (user.role === UserRole.SUPER_ADMIN) return '/admin/dashboard';
  return resolveGuideLifecycleRoute(user) ?? GUIDE_ENTRY_ROUTE;
}

/** @deprecated Use `resolveGuideLifecycleRoute`. */
export function resolveExistingGuideRoute(
  user: User,
  _permissions: Permission[]
): string | null {
  return resolveGuideLifecycleRoute(user);
}

export function resolvePostAuthRoute(
  user: User,
  permissions: Permission[],
  _isNewUser: boolean,
  from?: string,
  intent?: AuthIntent
): string {
  if (user.role === UserRole.SUPER_ADMIN) {
    return '/admin/dashboard';
  }

  // One decision for every guide-lifecycle user, whatever their intent.
  const guideRoute = resolveGuideLifecycleRoute(user);
  if (guideRoute) return guideRoute;

  if (intent === 'guide' || from === GUIDE_ENTRY_ROUTE) {
    return GUIDE_ENTRY_ROUTE;
  }

  if (from && !AUTH_PATHS.has(from)) {
    return from;
  }

  return '/';
}

/** @deprecated Legacy status accessor retained for compatibility. */
export function getGuideStatus(user: User | null) {
  return user?.guideProfile?.verificationStatus ?? user?.guideStatus;
}
